import React, { type FC } from 'react';

import { Alert, Progress, ProgressTheme } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { formatBytes } from 'client/lib/utils';
import { TQuickAppsMeta } from 'client/model/privateApps';
import * as keyset from 'i18n/PrivateApps';

import styles from './QuickAppsAvailableSpace.module.scss';

interface Props {
    meta: TQuickAppsMeta | undefined;
}

const FREE_SPACE_DEFAULT = 10737418240;

const t = i18n(keyset);

export const QuickAppsAvailableSpace: FC<Props> = ({ meta }) => {
    const availableSpaceB = meta?.available_size_b || FREE_SPACE_DEFAULT;
    const currentlyUsedSpaceB = meta?.current_size_b || 0;
    const freeSpaceB = availableSpaceB - currentlyUsedSpaceB;

    const availableSizeFormatted = formatBytes(availableSpaceB);
    const currentlyUsedSizeFormatted = formatBytes(currentlyUsedSpaceB);
    const freeSpaceFormatted = formatBytes(freeSpaceB);

    const progressValue = Math.ceil((currentlyUsedSpaceB / availableSpaceB) * 100) || 0;

    let progressTheme: ProgressTheme = 'info';
    if (progressValue >= 90) progressTheme = 'warning';
    if (progressValue > 97) progressTheme = 'danger';

    return (
        <>
            <div className={styles.quickAppsAvailableSpace__header}>
                {meta ? (
                    <>
                        <div>
                            {t('space-used', {
                                spaceAllocatedMb: currentlyUsedSizeFormatted,
                                spaceAvailableGb: availableSizeFormatted,
                            })}
                        </div>
                        <div className={styles.quickAppsAvailableSpace__headerRight}>
                            {t('space-free', { freeSpaceMb: freeSpaceFormatted })}
                        </div>
                    </>
                ) : null}
            </div>
            <Progress
                className={styles.quickAppsAvailableSpace__progress}
                theme={progressTheme}
                value={progressValue}
                size="s"
            />
            {progressValue > 97 && (
                <Alert
                    className={styles.quickAppsAvailableSpace__alert}
                    theme="danger"
                    title={t('storage-full')}
                    message={t('remove-unneeded-apps')}
                />
            )}
        </>
    );
};
